import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "Servizi", href: "#servizi" },
  { label: "Soluzioni NFC", href: "#nfc" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Chi siamo", href: "#chi-siamo" },
  { label: "Contatti", href: "#contatti" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-700 ${
          scrolled ? "glass-dark border-b border-[#C19D60]/15" : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 lg:px-12">
          <a href="#home" data-testid="nav-logo" className="flex items-center gap-3">
            <img src="/assets/recensio-logo.png" alt="Recensio" className="h-9 w-auto" />
          </a>

          <nav className="hidden items-center gap-9 lg:flex" data-testid="nav-menu">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative text-[13px] font-light tracking-[0.14em] text-[#c9c9c9] transition-colors duration-500 hover:text-[#F8F5F0]"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#C19D60] transition-[width] duration-500 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href="#contatti"
              data-testid="nav-quote-btn"
              className="group relative hidden overflow-hidden border border-[#C19D60]/40 px-6 py-2.5 text-[12px] font-medium uppercase tracking-[0.22em] text-[#E5CAA4] transition-colors duration-500 hover:text-[#050505] lg:inline-block"
            >
              <span className="absolute inset-0 origin-left scale-x-0 bg-[#C19D60] transition-transform duration-500 ease-out group-hover:scale-x-100" />
              <span className="relative">Richiedi un preventivo</span>
            </a>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(true)}
              className="text-[#F8F5F0] lg:hidden"
              aria-label="Apri menu"
            >
              <Menu size={26} strokeWidth={1.2} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-[70] flex flex-col bg-[#050505]/97 backdrop-blur-2xl"
          >
            <div className="flex items-center justify-between px-6 py-4">
              <img src="/assets/recensio-logo.png" alt="Recensio" className="h-9 w-auto" />
              <button data-testid="nav-mobile-close" onClick={() => setOpen(false)} aria-label="Chiudi menu" className="text-[#F8F5F0]">
                <X size={28} strokeWidth={1.2} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-10">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.07, duration: 0.6 }}
                  className="font-display py-3 text-4xl font-medium text-[#F8F5F0] transition-colors duration-300 hover:text-[#C19D60]"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href="#contatti"
                data-testid="nav-mobile-quote-btn"
                onClick={() => setOpen(false)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="mt-8 inline-block w-fit border border-[#C19D60]/50 px-8 py-3 text-[12px] uppercase tracking-[0.25em] text-[#E5CAA4]"
              >
                Richiedi un preventivo
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
